import { Box, Button, Flex, Grid, Stack, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import { useState, useMemo } from 'react';

import { SFDownloadOutlined } from '@assets/icons';
import ExplorerSourceCard from '@components/explorer/explorer-source-card.component';
import StyledModal from '@components/general/modals/styled-modal.component';
import { useBulkExportTranscripts } from '@services/query-services/meetings.query-service';
import { useStyledToast } from '@util/hooks';

/**
 * BulkExportModal component
 * Modal for bulk exporting summary cards with a scrollable 2-column layout
 * @param {Array} cards - Array of card objects to display and export
 * @param {boolean} isDisabled - Whether the button should be disabled
 * @param {string} tooltipText - Tooltip text to show when button is disabled
 * @param {Function} onExport - Callback function to handle the export action
 */
export default function BulkExportModal({
  cards = [],
  isDisabled = false,
  tooltipText = '',
  onExport
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useStyledToast();

  // Track removed card IDs instead of managing the full filtered list
  const [removedCardIds, setRemovedCardIds] = useState(new Set());

  const { mutateAsync: exportTranscripts, isLoading } = useBulkExportTranscripts();

  // Use useMemo to compute filtered cards, avoiding the infinite loop
  const filteredCards = useMemo(() => {
    return cards.filter((card) => !removedCardIds.has(card.id));
  }, [cards, removedCardIds]);

  // Function to remove a card from the list
  const removeCard = (cardId) => {
    setRemovedCardIds((prev) => new Set(prev.add(cardId)));
  };

  const handleExport = async () => {
    if (onExport) {
      onExport(filteredCards);
      onClose();
      return;
    }

    // Transform filteredCards to array of source_data.id strings
    const meetingIds = filteredCards
      .filter((card) => card?.source_data?.id)
      .map((card) => card.source_data.id);

    if (meetingIds.length === 0) {
      toast({
        title: 'No Valid Cards',
        description: 'No valid meeting cards found for export.',
        status: 'error'
      });
      return;
    }

    await exportTranscripts(meetingIds, {
      onSuccess: () => {
        toast({
          title: 'Export Started',
          description: `Successfully started bulk export for ${filteredCards.length} meeting transcripts.`,
          status: 'success'
        });

        onClose();
      },
      onError: () => {
        toast({
          title: 'Export Failed',
          description: 'Failed to start bulk export. Please try again.',
          status: 'error'
        });
      }
    });
  };

  return (
    <Tooltip
      isDisabled={!tooltipText}
      label={tooltipText}
    >
      <Box>
        <StyledModal
          title='Bulk Export'
          modalContentProps={{ maxW: '4xl' }}
          modalBodyProps={{ p: 6 }}
          disclosure={{ isOpen, onOpen, onClose }}
          openButton={
            <Button
              isDisabled={isDisabled}
              variant='reskinOutline'
              leftIcon={<SFDownloadOutlined boxSize={4} />}
              size='md'
              data-testid='open-modal-button'
            >
              Bulk Export
            </Button>
          }
        >
          <Stack spacing={6}>
            <Text variant='content-section-header'>
              Export {filteredCards?.length || 0} meeting transcripts
            </Text>

            <Box
              maxH='60vh'
              overflowY='auto'
            >
              <Grid
                templateColumns='1fr 1fr'
                gap={4}
              >
                {filteredCards?.map((card) => (
                  <ExplorerSourceCard
                    key={card.id}
                    card={card}
                    onRemove={() => removeCard(card.id)}
                  />
                ))}
              </Grid>
            </Box>

            <Flex
              justifyContent='space-between'
              pt={4}
            >
              <Button
                variant='reskinSecondary'
                size='lg'
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant='reskinPrimary'
                size='lg'
                onClick={handleExport}
                isLoading={isLoading}
                loadingText='Exporting...'
                data-testid='bulk-export-button'
              >
                Bulk Export
              </Button>
            </Flex>
          </Stack>
        </StyledModal>
      </Box>
    </Tooltip>
  );
}
